<template>
  <div class="recon-config">
    <EntraPageHeader
      title="Recon Config"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadConfig"
    />
    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>
      <div v-if="successMsg" class="success-message">{{ successMsg }}</div>
      <div v-if="nceWarnings.length > 0" class="warning-message">
        <div v-for="(warn, idx) in nceWarnings" :key="idx">{{ warn }}</div>
      </div>
      <div v-if="nceReport" class="card nce-report">
        <h3>NCE {{ nceReport.sourceMonth }} set import — {{ nceReport.totals.imported }} imported / {{ nceReport.totals.skipped }} skipped of {{ nceReport.totals.rows }} rows ({{ nceReport.totals.files }} files)</h3>
        <p class="report-note">Parity filters applied: Commercial segment only; currency allowlist (CAD+USD for CA files, GBP+USD otherwise). Skipped rows are excluded by design.</p>
        <table class="data-table">
          <thead>
            <tr>
              <th>File</th>
              <th style="text-align: right;">Rows</th>
              <th style="text-align: right;">Importable</th>
              <th style="text-align: right;">Non-Commercial</th>
              <th style="text-align: right;">Currency-Filtered</th>
              <th style="text-align: right;">Missing ID</th>
              <th>Markets</th>
              <th>Currencies</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in nceReport.files" :key="f.name">
              <td>{{ f.name }}</td>
              <td style="text-align: right;">{{ f.rows }}</td>
              <td style="text-align: right;">{{ f.importable }}</td>
              <td style="text-align: right;">{{ f.skipReasons['non-commercial'] }}</td>
              <td style="text-align: right;">{{ f.skipReasons['currency-filtered'] }}</td>
              <td style="text-align: right;">{{ f.skipReasons['missing-id'] }}</td>
              <td>{{ (f.markets || []).join(', ') }}</td>
              <td>{{ (f.currencies || []).join(', ') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="config-tabs">
        <button v-for="tab in tabGroups" :key="tab.key"
          :class="{ active: activeTab === tab.key }"
          @click="switchTab(tab.key)"
          class="tab-btn">
          {{ tab.label }}
        </button>
      </div>

      <div class="sub-tabs" v-if="activeGroupConfigs.length > 1">
        <button v-for="cfg in activeGroupConfigs" :key="cfg.key"
          :class="{ active: activeConfigKey === cfg.key }"
          @click="switchConfig(cfg.key)"
          class="sub-tab-btn">
          {{ cfg.label }}
        </button>
      </div>

      <div class="card" v-if="activeConfig">
        <div class="card-header-row">
          <h3>{{ activeGroupLabel }} — {{ activeConfig.label }}</h3>
          <div class="header-actions">
            <div v-if="activeConfigKey === 'currency-history'" class="fx-fetch-group">
              <input v-model="fxMonth" type="month" class="fx-month-input" />
              <button class="import-btn" :disabled="fetchingFx" @click="triggerFetchFx">
                {{ fetchingFx ? 'Fetching...' : 'Fetch from OANDA' }}
              </button>
            </div>
            <div v-if="activeConfigKey === 'nce-prices'" class="fx-fetch-group">
              <input v-model="nceMonth" type="month" class="fx-month-input" />
              <span class="action-info-wrap">
                <button class="import-btn primary" :disabled="nceSetImporting" @click="$refs.nceSetFileInput.click()">
                  {{ nceSetImporting ? 'Importing...' : 'Import Monthly Price Set' }}
                </button>
                <button class="info-btn" type="button" aria-label="What does this import do?" @click.stop="toggleNceInfo('set')">i</button>
                <div v-if="nceInfoOpen === 'set'" class="info-pop" @click.stop>
                  <strong>Import Monthly Price Set</strong>
                  <p>Replaces everything tagged {{ nceMonth }}: all existing rows for that month are deleted, then the files you pick are inserted as one clean set.</p>
                  <p>Select all six market files (AE, CA, GB, HK, KY, US) together — missing or duplicated markets come back as warnings, and Recon Processing stays blocked for the month until its set is complete.</p>
                  <p>This is the normal monthly flow, done once per month after downloading the price lists from Partner Center.</p>
                </div>
              </span>
              <span class="action-info-wrap">
                <button class="import-btn secondary" @click="$refs.nceFileInput.click()">Add Single File…</button>
                <button class="info-btn" type="button" aria-label="What does this import do?" @click.stop="toggleNceInfo('file')">i</button>
                <div v-if="nceInfoOpen === 'file'" class="info-pop" @click.stop>
                  <strong>Add Single File</strong>
                  <p>Merges one market file into the {{ nceMonth }} set: matching rows (same SKU + start date) are updated, new rows are added, everything else is left untouched.</p>
                  <p>Recovery/correction path only — e.g. re-importing one market that had a bad file. Not a shortcut for the monthly import: uploading fewer than six files leaves the set incomplete and the pricing gate blocked.</p>
                </div>
              </span>
            </div>
            <button v-if="!isImportOnly" class="add-btn" @click="startAdd">+ Add Row</button>
          </div>
        </div>
        <input type="file" ref="nceFileInput" accept=".csv" class="file-input" @change="handleNceUpload" />
        <input type="file" ref="nceSetFileInput" accept=".csv" multiple class="file-input" @change="handleNceSetUpload" />

        <div v-if="activeConfigKey === 'sku-renaming'" class="coverage-block">
          <h4 class="coverage-title">Suggested renaming rules (first billable sightings)</h4>
          <div class="suggestion-controls">
            <input v-model="suggestionMonth" type="month" class="fx-month-input" @change="loadSkuSuggestions" />
            <span class="report-note">Sightings with no rule from {{ suggestionMonth }}; suggestions come from the NCE price list.</span>
          </div>
          <div v-if="suggestionsLoading" class="loading-state"><span>Loading suggestions...</span></div>
          <table class="data-table" v-else-if="suggestions.length > 0">
            <thead>
              <tr>
                <th>Raw SKU (as billed)</th>
                <th style="text-align: right;">Lines</th>
                <th style="text-align: right;">Clients</th>
                <th>Suggested display name</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in suggestions" :key="item.skuName + '|' + idx">
                <td>{{ item.skuName }}</td>
                <td style="text-align: right;">{{ item.lines }}</td>
                <td style="text-align: right;">{{ item.clients }}</td>
                <td>
                  <template v-if="item.suggestion">
                    <strong>{{ item.suggestion.cleanSkuName }}</strong>
                    <div class="suggestion-note">{{ item.suggestion.note }}</div>
                  </template>
                  <span v-else class="report-note">No price-list match — name it manually.</span>
                </td>
                <td>{{ item.suggestion ? item.suggestion.source : '--' }}</td>
                <td>
                  <button v-if="item.suggestion" class="import-btn" :disabled="item.applying || item.applied" @click="applySuggestion(item)">
                    {{ item.applied ? 'Applied' : (item.applying ? 'Applying...' : 'Apply rule') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">No unnamed sightings for {{ suggestionMonth }}.</p>
        </div>

        <div v-if="activeConfigKey === 'nce-prices'" class="coverage-block">
          <p class="report-note coverage-help">Each billing month needs its own price set of the six market files (AE, CA, GB, HK, KY, US) downloaded from Partner Center. Pick the month above, select all six files, and use <strong>Import Monthly Price Set</strong> — that month's rows are replaced as one clean set. Recon Processing blocks a month's processing and generation until its set is complete.</p>
          <h4 class="coverage-title">Current coverage by source month</h4>
          <table class="data-table" v-if="nceCoverage.length > 0">
            <thead>
              <tr>
                <th>Source</th>
                <th>Market</th>
                <th>Currency</th>
                <th style="text-align: right;">Rows</th>
                <th>Earliest Start</th>
                <th>Latest Start</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in nceCoverage" :key="row.source + '|' + row.market + '|' + row.currency + '|' + idx">
                <td>{{ row.source }}</td>
                <td>{{ row.market || '--' }}</td>
                <td>{{ row.currency }}</td>
                <td style="text-align: right;">{{ row.rowCount }}</td>
                <td>{{ row.earliestStart || '--' }}</td>
                <td>{{ row.latestStart || '--' }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">No price coverage loaded.</p>
          <p v-if="hasWbSnap" class="report-note wbsnap-note">
            <strong>wb-snap</strong> is the historical baseline imported from the legacy macro workbook when the portal went live — it still prices months before 2026-06 and is superseded by the real 2026-06+ sets for those months. "—" market means the workbook sheet had no market column, so its rows carry currency only.
          </p>
        </div>

        <div v-if="configState === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else-if="configRows && configRows.length > 0" class="config-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th v-for="col in activeConfig.columns.filter(c => c !== 'id' && c !== 'created_at' && c !== 'updated_at')" :key="col">
                  {{ col.replace(/_/g, ' ') }}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in configRows" :key="idx">
                <td v-for="col in activeConfig.columns.filter(c => c !== 'id' && c !== 'created_at' && c !== 'updated_at')" :key="col">
                  {{ formatCell(row[col]) }}
                </td>
                <td>
                  <button v-if="!isImportOnly" class="edit-btn" @click="startEdit(idx, row)">Edit</button>
                  <button class="delete-btn" @click="deleteRow(idx, row)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="no-data">No config data.{{ isImportOnly ? ' Import a set to populate it.' : ' Click "+ Add Row" to create one.' }}</p>
      </div>

      <ReconConfigRowModal
        :show="rowModal.show"
        :configKey="activeConfigKey"
        :configLabel="activeConfig ? activeConfig.label : ''"
        :fields="modalFields"
        :groups="modalGroups"
        :upsertNote="modalUpsertNote"
        :mode="rowModal.mode"
        :initialRow="rowModal.initialRow"
        @save="handleRowSave"
        @close="rowModal.show = false"
      />

      <div class="card" v-if="activeTab === 'defaults'">
        <h3>Default Config (Key Variables)</h3>
        <div v-if="defaultsState === 'loading'" class="loading-state"><span>Loading...</span></div>
        <div v-else class="defaults-grid">
          <div v-for="(value, key) in defaultsData" :key="key" class="default-row">
            <label class="filter-label">{{ key.replace(/_/g, ' ') }}</label>
            <input v-model="defaultsData[key]" class="filter-input" />
          </div>
          <button class="save-btn" @click="saveDefaults">Save Defaults</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import ReconConfigRowModal from '@/views/components/ReconConfigRowModal.vue';
import {
  getFieldDefs,
  getUniqueColumn
} from '@/views/billing/recon-config-fields.js';
import {
  getReconConfig,
  saveReconConfig,
  updateReconConfig,
  deleteReconConfig,
  getReconDefaults,
  saveReconDefaults,
  importNcePrices,
  importNcePriceSet,
  getNcePriceCoverage,
  fetchFxRates,
  getSkuSuggestions
} from '@/services/billing-api.js';

var CONFIG_DEFS = {
  'monthly': [
    { key: 'currency-history', label: 'Currency History (FX Rates)', columns: ['month','usd','gbp','cad','hkd','eur','aud','chf','jpy','sgd'] },
    { key: 'posting-months', label: 'Posting Months', columns: ['billing_month','posting_date'] },
    { key: 'nce-prices', label: 'NCE Prices', columns: ['id','source','product_id','sku_id','product_name','sku_name','unit_price','effective_unit_price','discount','price_per_cycle','start_date','end_date','commitment','billed_term','currency','billing_frequency','full_sku','sub_sku'] }
  ],
  'client': [
    { key: 'intacct-mapping', label: 'Intacct Mapping', columns: ['id','client_name','bill_split','locations','split_m365','split_azure','tax_type','tax_rate','intacct_codes','billing_entities','currencies','generate_bill','class_id','entity_name','cust_name_clean','cust_name_match','parent_id','business_unit'] },
    { key: 'billing-exclusions', label: 'Client Billing Dates', columns: ['id','client_name','m365_billing_start','m365_billing_end','azure_billing_start','azure_billing_end','opp_id','notes'] },
    { key: 'license-adjustments', label: 'License Adjustments', columns: ['id','client_name','sku_name','adjustment_amount','term','start_month','end_month','reason','removal_date'] },
    { key: 'license-exclusions', label: 'License Exclusions', columns: ['id','customer_id','sku_name','term','reason'] },
    { key: 'azure-management-fees', label: 'Azure Management Fees', columns: ['id','client_name','fee_pct','min_fee','max_fee','start_month','end_month'] },
    { key: 'azure-additional-services', label: 'Azure Additional Services', columns: ['id','client_name','service_name','description','price','currency','quantity','charge_type','start_month','end_month'] },
    { key: 'invoice-memo-rules', label: 'Invoice Memo Rules', columns: ['id','client_name','applies_to','rule','value','start_month','end_month','notes'] }
  ],
  'static': [
    { key: 'entity-map', label: 'Intacct Entity Map', columns: ['country','entity','msft_id','csp_region'] },
    { key: 'sku-renaming', label: 'SKU Renaming', columns: ['id','trigger_type','trigger_value','clean_sku_name'] },
    { key: 'discounts', label: 'Discount Pairs (M365)', columns: ['id','match_type','match_value','discount_pct'] },
    { key: 'client-name-overrides', label: 'Client Name Overrides', columns: ['id','old_name','filter_column','filter_value','new_name'] },
    { key: 'ns-customer-mapping', label: 'NS Customer Mapping', columns: ['id','customer_id','customer_name','parent_customer_id','parent_customer_name'] }
  ]
};

export default {
  name: 'ReconConfig',
  components: { EntraPageHeader, ReconConfigRowModal },
  data: function() {
    return {
      error: '',
      successMsg: '',
      activeTab: 'monthly',
      tabGroups: [
        { key: 'monthly', label: 'Monthly' },
        { key: 'client', label: 'Client Config' },
        { key: 'static', label: 'Static Config' },
        { key: 'defaults', label: 'Defaults' }
      ],
      activeConfigKey: 'currency-history',
      configState: 'loaded',
      configRows: [],
      rowModal: {
        show: false,
        mode: 'add',
        initialRow: null
      },
      defaultsState: 'loaded',
      defaultsData: {},
      fxMonth: new Date().toISOString().slice(0, 7),
      nceMonth: new Date().toISOString().slice(0, 7),
      suggestionMonth: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().slice(0, 7),
      suggestions: [],
      suggestionsLoading: false,
      fetchingFx: false,
      nceSetImporting: false,
      nceReport: null,
      nceWarnings: [],
      nceCoverage: [],
      nceInfoOpen: ''
    };
  },
  computed: {
    activeGroupConfigs: function() {
      return CONFIG_DEFS[this.activeTab] || [];
    },
    activeConfig: function() {
      var configs = this.activeGroupConfigs;
      for (var i = 0; i < configs.length; i++) {
        if (configs[i].key === this.activeConfigKey) return configs[i];
      }
      return configs[0] || null;
    },
    activeGroupLabel: function() {
      var labels = { monthly: 'Monthly Config', client: 'Client Config', static: 'Static Config', defaults: 'Defaults' };
      return labels[this.activeTab] || 'Config';
    },
    isImportOnly: function() {
      var defs = getFieldDefs(this.activeConfigKey);
      return !!(defs && defs.importOnly);
    },
    modalFields: function() {
      var defs = getFieldDefs(this.activeConfigKey);
      return defs ? defs.fields : [];
    },
    modalGroups: function() {
      var defs = getFieldDefs(this.activeConfigKey);
      return defs && defs.groups ? defs.groups : null;
    },
    modalUpsertNote: function() {
      var defs = getFieldDefs(this.activeConfigKey);
      return defs && defs.upsertNote ? defs.upsertNote : '';
    },
    hasWbSnap: function() {
      return this.nceCoverage.some(function(row) { return row.source === 'wb-snap'; });
    }
  },
  methods: {
    toggleNceInfo: function(which) {
      this.nceInfoOpen = this.nceInfoOpen === which ? '' : which;
    },
    closeNceInfo: function() {
      this.nceInfoOpen = '';
    },
    formatCell: function(val) {
      if (val === null || val === undefined) return '';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      return String(val);
    },
    switchTab: function(tabKey) {
      this.activeTab = tabKey;
      var configs = CONFIG_DEFS[tabKey];
      if (configs && configs.length > 0) {
        this.activeConfigKey = configs[0].key;
      }
      this.loadConfig();
    },
    switchConfig: function(configKey) {
      this.activeConfigKey = configKey;
      this.nceReport = null;
      this.nceWarnings = [];
      if (configKey === 'nce-prices') this.loadCoverage();
      if (configKey === 'sku-renaming') this.loadSkuSuggestions();
      this.loadConfig();
    },
    loadSkuSuggestions: function() {
      var self = this;
      if (!self.suggestionMonth) return;
      self.suggestionsLoading = true;
      getSkuSuggestions({ billingMonth: self.suggestionMonth }).then(function(data) {
        self.suggestions = data.items || [];
        self.suggestionsLoading = false;
      }).catch(function(err) {
        self.suggestions = [];
        self.suggestionsLoading = false;
        self.error = err.message || 'Failed to load SKU suggestions.';
      });
    },
    applySuggestion: function(item) {
      var self = this;
      if (!item.suggestion || item.applying || item.applied) return;
      item.applying = true;
      saveReconConfig('sku-renaming', {
        trigger_type: item.suggestion.triggerType,
        trigger_value: item.suggestion.triggerValue,
        clean_sku_name: item.suggestion.cleanSkuName
      }).then(function() {
        item.applying = false;
        item.applied = true;
        self.successMsg = 'Rule added: ' + item.suggestion.triggerValue + ' → ' + item.suggestion.cleanSkuName;
        self.loadConfig();
        setTimeout(function() { self.successMsg = ''; }, 6000);
        self.loadSkuSuggestions();
      }).catch(function(err) {
        item.applying = false;
        self.error = err.message || 'Failed to add renaming rule.';
      });
    },
    loadCoverage: function() {
      var self = this;
      getNcePriceCoverage().then(function(data) {
        self.nceCoverage = data.items || [];
      }).catch(function() {
        self.nceCoverage = [];
      });
    },
    handleNceSetUpload: function(event) {
      var self = this;
      var fileList = Array.prototype.slice.call(event.target.files || []);
      event.target.value = '';
      if (fileList.length === 0) return;
      if (!confirm('Import ' + fileList.length + ' file(s) as the ' + self.nceMonth + ' NCE set? This purges and replaces ALL price rows for source month ' + self.nceMonth + '.')) return;
      self.nceSetImporting = true;
      self.error = '';
      self.nceWarnings = [];
      var readers = fileList.map(function(file) {
        return new Promise(function(resolve, reject) {
          var reader = new FileReader();
          reader.onload = function(e) { resolve({ name: file.name, content: e.target.result }); };
          reader.onerror = function() { reject(new Error('Failed to read ' + file.name)); };
          reader.readAsText(file);
        });
      });
      Promise.all(readers).then(function(files) {
        return importNcePriceSet(self.nceMonth, files);
      }).then(function(report) {
        self.nceReport = report;
        self.nceWarnings = report.warnings || [];
        self.successMsg = 'NCE ' + report.sourceMonth + ' set imported: ' + report.totals.imported + ' imported, ' + report.totals.skipped + ' skipped across ' + report.totals.files + ' files [' + (report.markets || []).join(',') + ']';
        self.nceSetImporting = false;
        self.loadConfig();
        self.loadCoverage();
        setTimeout(function() { self.successMsg = ''; }, 8000);
      }).catch(function(err) {
        self.error = err.message || 'NCE set import failed.';
        self.nceSetImporting = false;
      });
    },
    handleNceUpload: function(event) {
      var self = this;
      var file = event.target.files[0];
      if (!file) return;
      var reader = new FileReader();
      reader.onload = function(e) {
        importNcePrices(e.target.result, self.nceMonth).then(function(data) {
          var marketInfo = data.markets && data.markets.length ? ' [' + data.markets.join(',') + ' / ' + (data.currencies || []).join(',') + ']' : '';
          self.successMsg = 'NCE ' + (data.sourceMonth || self.nceMonth) + ': ' + data.imported + ' imported, ' + data.skipped + ' skipped' + marketInfo;
          self.loadConfig();
          self.loadCoverage();
          setTimeout(function() { self.successMsg = ''; }, 5000);
        }).catch(function(err) {
          self.error = err.message || 'NCE import failed.';
        });
      };
      reader.readAsText(file);
      event.target.value = '';
    },
    triggerFetchFx: function() {
      var self = this;
      this.fetchingFx = true;
      this.error = '';
      fetchFxRates(this.fxMonth).then(function(data) {
        var msg = 'FX rates fetched for ' + self.fxMonth + ' (rate date ' + (data.rateDate || '?') + ')';
        if (data.warnings && data.warnings.length > 0) msg += ' — ' + data.warnings.join('; ');
        self.successMsg = msg;
        self.fetchingFx = false;
        self.loadConfig();
        setTimeout(function() { self.successMsg = ''; }, 8000);
      }).catch(function(err) {
        self.error = err.message || 'FX fetch failed.';
        self.fetchingFx = false;
      });
    },
    loadConfig: function() {
      if (this.activeTab === 'defaults') {
        this.loadDefaults();
        return;
      }
      var self = this;
      var key = this.activeConfigKey;
      this.configState = 'loading';
      this.rowModal.show = false;
      getReconConfig(key).then(function(data) {
        self.configRows = data.items || [];
        self.configState = 'loaded';
      }).catch(function(err) {
        self.error = err.message || 'Failed to load config.';
        self.configState = 'loaded';
      });
    },
    loadDefaults: function() {
      var self = this;
      this.defaultsState = 'loading';
      getReconDefaults().then(function(data) {
        self.defaultsData = data.defaults || {};
        self.defaultsState = 'loaded';
      }).catch(function(err) {
        self.error = err.message;
        self.defaultsState = 'loaded';
      });
    },
    startEdit: function(idx, row) {
      this.rowModal = { show: true, mode: 'edit', initialRow: Object.assign({}, row) };
    },
    startAdd: function() {
      this.rowModal = { show: true, mode: 'add', initialRow: null };
    },
    handleRowSave: function(evt) {
      var self = this;
      var key = this.activeConfigKey;
      var uniqueCol = getUniqueColumn(key);
      if (this.rowModal.mode === 'edit') {
        var idVal = this.rowModal.initialRow ? this.rowModal.initialRow[uniqueCol] : null;
        if (idVal === null || idVal === undefined || idVal === '') {
          evt.fail('Cannot save: missing unique column "' + uniqueCol + '".');
          return;
        }
        updateReconConfig(key, idVal, evt.payload).then(function() {
          self.successMsg = 'Row saved.';
          evt.done();
          self.loadConfig();
          setTimeout(function() { self.successMsg = ''; }, 3000);
        }).catch(function(err) {
          evt.fail(err.message || 'Save failed.', err.target);
        });
      } else {
        saveReconConfig(key, evt.payload).then(function() {
          self.successMsg = 'Row added.';
          evt.done();
          self.loadConfig();
          setTimeout(function() { self.successMsg = ''; }, 3000);
        }).catch(function(err) {
          evt.fail(err.message || 'Add failed.', err.target);
        });
      }
    },
    deleteRow: function(idx, row) {
      var self = this;
      if (!confirm('Delete this row?')) return;
      var key = this.activeConfigKey;
      var uniqueCol = getUniqueColumn(key);
      var id = row[uniqueCol];
      if (id === null || id === undefined || id === '') {
        self.error = 'Cannot delete: missing unique column "' + uniqueCol + '".';
        return;
      }
      deleteReconConfig(key, id).then(function() {
        self.successMsg = 'Row deleted.';
        self.loadConfig();
        setTimeout(function() { self.successMsg = ''; }, 3000);
      }).catch(function(err) {
        self.error = err.message || 'Delete failed.';
      });
    },
    saveDefaults: function() {
      var self = this;
      saveReconDefaults(this.defaultsData).then(function() {
        self.successMsg = 'Defaults saved.';
        setTimeout(function() { self.successMsg = ''; }, 3000);
      }).catch(function(err) {
        self.error = err.message;
      });
    }
  },
  mounted: function() {
    this.loadConfig();
    document.addEventListener('click', this.closeNceInfo);
  },
  beforeUnmount: function() {
    document.removeEventListener('click', this.closeNceInfo);
  }
};
</script>

<style scoped>
.recon-config { min-height: 100%; }
.page-body { padding: 24px; }
.config-tabs { display: flex; gap: 4px; margin-bottom: 20px; }
.tab-btn {
  background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 8px 20px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.tab-btn.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.sub-tabs { display: flex; gap: 4px; margin-bottom: 12px; flex-wrap: wrap; }
.sub-tab-btn {
  background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; padding: 6px 14px;
  border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.sub-tab-btn.active { background: #e0e7ff; color: #4338ca; border-color: #c7d2fe; }
.card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin-bottom: 20px;
}
.card h3 { font-size: 0.9375rem; font-weight: 700; color: #0f172a; margin: 0 0 16px 0; }
.card-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.card-header-row h3 { margin: 0; }
.header-actions { display: flex; gap: 8px; align-items: center; }
.fx-fetch-group { display: flex; gap: 8px; align-items: center; }
.fx-month-input {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 5px 10px;
  font-size: 0.8125rem; font-family: 'Inter', sans-serif;
}
.add-btn {
  background: #4f46e5; color: #fff; border: none; padding: 6px 16px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
}
.import-btn {
  background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 6px 16px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
}
.import-btn.primary { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.import-btn.primary:disabled { background: #a5b4fc; border-color: #a5b4fc; cursor: not-allowed; }
.import-btn.secondary { color: #64748b; }
.action-info-wrap { position: relative; display: inline-flex; align-items: center; gap: 5px; }
.info-btn {
  width: 18px; height: 18px; border-radius: 50%; border: 1px solid #cbd5e1;
  background: #fff; color: #64748b; font-size: 0.6875rem; font-weight: 700;
  font-family: Georgia, serif; font-style: italic; line-height: 1; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; padding: 0;
}
.info-btn:hover, .info-btn:focus-visible { border-color: #4f46e5; color: #4f46e5; }
.info-pop {
  position: absolute; top: calc(100% + 6px); right: 0; z-index: 20;
  width: 320px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12); padding: 12px 14px;
  font-size: 0.75rem; color: #334155; text-align: left;
}
.info-pop strong { display: block; font-size: 0.8125rem; color: #0f172a; }
.info-pop p { margin: 8px 0 0 0; line-height: 1.5; }
.file-input { display: none; }
.config-table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.8125rem; }
.data-table th { text-align: left; padding: 10px 12px; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr:last-child td { border-bottom: none; }
.cell-input {
  border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 8px;
  font-size: 0.8125rem; font-family: 'Inter', sans-serif; width: 100%; min-width: 80px;
}
.edit-btn, .delete-btn, .save-btn, .cancel-btn {
  border: 1px solid #e2e8f0; padding: 4px 12px; border-radius: 6px;
  font-size: 0.75rem; font-weight: 600; cursor: pointer; margin-right: 4px;
}
.edit-btn { background: #f1f5f9; color: #4f46e5; }
.save-btn { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
.cancel-btn { background: #f1f5f9; color: #64748b; }
.delete-btn { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; margin-bottom: 4px; display: block; }
.filter-input {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px;
  font-size: 0.875rem; font-family: 'Inter', sans-serif;
}
.defaults-grid { display: flex; flex-direction: column; gap: 16px; max-width: 400px; }
.default-row { display: flex; flex-direction: column; gap: 4px; }
.no-data { font-size: 0.875rem; color: #94a3b8; text-align: center; padding: 32px 16px; background: #f8fafc; border-radius: 8px; border: 1px dashed #e2e8f0; }
.info-note { font-size: 0.8125rem; color: #6366f1; background: #eef2ff; border: 1px solid #c7d2fe; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; }
.warning-message { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.nce-report h3 { font-size: 0.875rem; }
.report-note { font-size: 0.75rem; color: #64748b; margin: -8px 0 12px 0; }
.coverage-block { margin: 16px 0; }
.coverage-title { font-size: 0.8125rem; font-weight: 700; color: #475569; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.04em; }
.suggestion-controls { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.suggestion-controls .report-note { margin: 0; }
.suggestion-note { font-size: 0.75rem; color: #64748b; margin: 2px 0 0 0; }
.coverage-block .data-table td { vertical-align: top; }
.coverage-block .data-table td:last-child, .coverage-block .data-table th:last-child { white-space: nowrap; }
.coverage-help { margin: 0 0 12px 0; max-width: 900px; line-height: 1.5; }
.wbsnap-note { margin: 10px 0 0 0; max-width: 900px; line-height: 1.5; }
.error-message { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.success-message { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.loading-state { color: #64748b; font-size: 0.875rem; padding: 24px; }
</style>
